import {useEffect, useState} from "react";

/* Hydration guard — matches the prototype so time-relative labels render a
 * stable placeholder on first paint before the client clock is trusted. */
export function useHydrated() {
  const [h, setH] = useState(false);
  useEffect(() => setH(true), []);
  return h;
}

export function vehicleLabel(row) {
  const parts = [row?.vehicle, row?.carNotes].filter(Boolean);

  return parts.length ? parts.join(" · ") : null;
}

export const PLATE_IMAGE_BG = "linear-gradient(135deg, #4a3f38 0%, #2f2822 60%, #1e1a16 100%)";

// Blank text columns default to "" in the legacy schema, so trim before the UI
// treats a value as present.
function text(value) {
  const trimmed = typeof value === "string" ? value.trim() : value;

  return trimmed || null;
}

// The guest columns verbatim, keyed as the booking mutations name them - the
// edit form reads and writes them straight back.
export const GUEST_COLUMNS = [
  "guestName",
  "guestPhone",
  "guestEmail",
  "guestRoom",
  "guestApartmentNumber",
  "guestReservation",
  "guestCompany",
  "guestDepartment",
  "guestEmployeeCode",
  "guestPosition",
  "carType",
  "carNotes",
];

function guestColumns(booking) {
  return GUEST_COLUMNS.reduce(
    (acc, key) => ({ ...acc, [key]: booking[key] ?? "" }),
    {},
  );
}

/*
 * Whether anything on this card is ever charged to a guest.
 *
 * A card with paymentStripe=false is a chargeback card: the hotel invoices the
 * guest for the stay, the pickup fee and the extras alike, so nothing here is
 * ever collected at the desk. Fails closed - a card that did not load is
 * treated as one that bills, rather than telling an operator nothing is owed.
 */
export function isPaymentRequired(rateCard) {
  return rateCard ? rateCard.paymentStripe !== false : true;
}

/* Whether the guest owes us anything at all — the question the badges ask.
 * The pickup fee used to split this from isPaymentRequired; it no longer does,
 * because on a chargeback card the fee goes on the hotel's invoice with
 * everything else. What the hotel will invoice is the server's to price - see
 * the charge payload's `hotelInvoiced`. */
export function billsGuest(rateCard) {
  return isPaymentRequired(rateCard);
}

/*
 * Why a car on the forecourt is allowed to be here.
 *
 * Three separate states, and this used to collapse two of them. "Sanctioned"
 * is the installation's plate whitelist - a regular waved through without a
 * reservation - and never meant "has a booking", which is what the row flag
 * actually held. So every pre-booked guest was badged Sanctioned, and a real
 * sanctioned parker, having no booking, was badged the opposite.
 */
export function arrivalBadges(row) {
  const badges = [];

  if (row?.hasBooking) badges.push({ label: "Pre-booked", tone: "ok" });

  /* Both can be true - a regular who also reserved - and the desk wants to
   * know, so this is not an else. */
  if (row?.sanctionedPlate) badges.push({ label: "Sanctioned", tone: "ok" });

  if (badges.length === 0) badges.push({ label: "No booking", tone: "bad" });

  return badges;
}

/*
 * A car with neither a booking nor a place on the whitelist: the one the desk
 * has to resolve before anything else can happen to it.
 */
export function isUnmatched(row) {
  return !row?.hasBooking && !row?.sanctionedPlate;
}

/** How the sanctioned entry describes this car, for the desk to read. */
export function sanctionedPlateLabel(row) {
  const entry = row?.sanctionedPlate;

  if (!entry) return null;

  return [entry.label, entry.lotName].filter(Boolean).join(" · ") || entry.plate;
}

/** The label, so "Unpaid" can never be shown for a card we do not charge. */
export function paymentTag(row) {
  if (row?.paymentRequired === false) {
    return { label: "No payment due", tone: "muted" };
  }
  if (row?.paid === true) return { label: "Paid", tone: "ok" };
  if (row?.paymentLinkSent) return { label: "Link sent", tone: "warn" };

  return { label: "Unpaid", tone: "bad" };
}

function toAmount(amountAud) {
  if (amountAud === null || amountAud === undefined) return null;

  const amount = Number(amountAud);

  return Number.isFinite(amount) ? amount : null;
}

/*
 * The runner queue's narrow booking, mapped into the same row shape the full one
 * produces. Everything the desk-only fields would have filled - contact details,
 * card and charge state, the guest edit form's inputs - is simply absent, and no
 * runner view reads them.
 */
export function mapRunnerBookingToArrival(booking) {
  return {
    /* Says the desk-only fields are absent because the server withheld them,
       not because the booking has none. */
    narrow: true,
    id: booking.id,
    bookingId: booking.id,
    plate: booking.plate,
    guestName: booking.guestName,
    notes: text(booking.carNotes),
    carNotes: text(booking.carNotes),
    vehicle: text(booking.carType),

    expectedDeparture: booking.expectedDepartureAt ?? null,

    /* Already flattened server-side: the runner never receives the rate card. */
    cardLotId: booking.lotId ?? null,
    lotName: booking.lotName ?? null,
    lotType: booking.lotType ?? null,
    vip: booking.isVip === true,
    paymentRequired: booking.paymentRequired !== false,
    paid: booking.paid === true,

    bookingStatus: booking.status ?? null,

    hasBooking: true,
    /* The runner projection carries no whitelist lookup: a runner works cars
     * the desk has already accepted, so the question is already settled. */
    sanctionedPlate: null,
  };
}

/*
 * The full booking laid over a row the runner queue only had the narrow
 * projection for. Only values the booking actually carries are overlaid: a
 * partial result must never blank a field the row already had. The session
 * keeps its own identity and the plate the camera read.
 */
export function withFullBooking(row, booking) {
  if (!booking) return row;

  const full = mapBookingToArrival(booking);
  const overlay = Object.fromEntries(
    Object.entries(full).filter(([, value]) => value !== undefined),
  );

  return {
    ...row,
    ...overlay,
    id: row.id,
    plate: row.plate ?? overlay.plate ?? null,
  };
}

export function mapSessionToArrival(session) {
  /* Two booking shapes reach this: the desk gets the full type, the runner queue
   * gets the narrow projection. Same row out either way. */
  const booking = session.booking ?? session.runnerBooking ?? null;
  const fromBooking = session.booking
    ? mapBookingToArrival(session.booking)
    : session.runnerBooking
      ? mapRunnerBookingToArrival(session.runnerBooking)
      : {};

  return {
    ...fromBooking,

    // The session, not the booking, is the identity of a car on the forecourt:
    // an unmatched arrival has no booking id at all.
    id: session.id,
    valetSessionId: session.id,
    bookingId: booking?.id ?? null,

    // Camera read wins over the booking's guest-entered plate.
    plate: session.plate ?? fromBooking.plate ?? null,
    guestName: fromBooking.guestName ?? "",
    notes: session.notes ?? fromBooking.notes ?? null,
    carNotes: fromBooking.carNotes ?? null,

    amount: fromBooking.amount ?? null,
    payLater: fromBooking.payLater === true,
    rateCard: fromBooking.rateCard ?? null,
    checkIn: fromBooking.checkIn ?? null,
    checkOut: fromBooking.checkOut ?? null,
    expectedDeparture: fromBooking.expectedDeparture ?? null,
    expectedArrival: fromBooking.expectedArrival ?? null,
    arrivalMode: fromBooking.arrivalMode ?? null,
    phone: fromBooking.phone || null,
    email: fromBooking.email || null,
    paymentRequired: fromBooking.paymentRequired !== false,
    paid: fromBooking.paid === true,
    vip: fromBooking.vip === true,
    vipContacts: fromBooking.vipContacts ?? [],
    vipAlertedAt: fromBooking.vipAlertedAt ?? null,

    status: session.status,
    arrivedAt: session.arrivedAt,
    arrivedGate: session.entryGateDescription ?? null,

    // Set once a concierge has taken the keys.
    claimedAt: session.claimedAt ?? null,

    // The runner driving the car to a bay, null until one takes it. Every
    // tablet at the facility sees every session, so this is what tells them
    // apart from the one this tablet is working.
    parkedBy: session.parkedByUserId ?? null,

    parkedByName: session.parkedByUser?.name ?? null,

    pickedBy: session.pickedByUserId ?? null,
    pickedByName: session.pickedByUser?.name ?? null,

    parkedAt: session.parkedAt ?? null,
    // The bay, when the runner recorded one. Written under its own name since
    // the lot selector landed; parkedLocation is the same value for the screens
    // that predate it.
    bay: session.bay ?? session.parkedLocation ?? null,
    lotId: session.lotId ?? null,
    /* Where the car actually went, which is not always where the guest card
     * pointed it - that one stays lotName/lotType off the booking. */
    parkedLotName: session.lot?.name ?? null,
    parkedLotType: session.lot?.type ?? null,
    /* The offsite facility's own ticket, written down by the runner. Distinct
     * from the booking's issued offsiteTicket record. */
    parkedTicket: session.offsiteTicket ?? null,

    requestedAt: session.requestedAt ?? null,
    pickupRequestedAt: session.pickupCommittedAt ?? null,
    /* The pickup ladder, one stamp per runner tap. */
    returningAt: session.returningAt ?? null,
    enRouteAt: session.enRouteAt ?? null,
    locatedAt: session.locatedAt ?? null,
    enRouteToGuestAt: session.enRouteToGuestAt ?? null,
    readyAt: session.readyAt ?? null,
    returnedAt: session.returnedAt ?? null,

    hasBooking: Boolean(booking),
    /* The installation's plate whitelist, matched server-side on the OCR-folded
     * plate. Null for every car that is not on it. */
    sanctionedPlate: session.sanctionedPlate ?? null,
  };
}

/** A pickup asked for ahead of when the car is wanted, rather than for now. */
export function isBookedAhead(row) {
  const wanted = new Date(row?.requestedAt ?? NaN).getTime();
  const asked = new Date(row?.pickupRequestedAt ?? NaN).getTime();

  return (
    Number.isFinite(wanted) &&
    Number.isFinite(asked) &&
    wanted - asked > 60 * 1000
  );
}

export function arrivalEta(booking) {
  return booking?.expectedArrival ?? booking?.checkIn ?? null;
}

export function vipContactIds(booking) {
  return (booking?.vipContacts ?? []).map((v) => v.contactId);
}

export function pagedVipContactIds(booking) {
  return (booking?.vipContacts ?? [])
    .filter((v) => v.alertedAt)
    .map((v) => v.contactId);
}

export function mapBookingToArrival(booking) {
  const rateCard = booking.rateCard;
  const payment = booking.paymentState;

  return {
    narrow: false,
    id: booking.id,
    bookingId: booking.id,
    plate: booking.plate,
    guestName: booking.guestName,
    email: text(booking.guestEmail),
    phone: text(booking.guestPhone),
    notes: text(booking.carNotes),
    carNotes: text(booking.carNotes),
    vehicle: text(booking.carType),

    // checkIn drives the arrival ETA, checkOut is the scheduled departure.
    checkIn: booking.startDay,
    checkOut: booking.endDay,

    /* Roughly when the guest says they will leave, as opposed to what the card
     * sells. An estimate: it calls nothing up, and a booked pickup lives on
     * the session as requestedAt. Kept separate from checkOut, which
     * stays the entitlement the fee engine prices. */
    expectedDeparture: booking.expectedDepartureAt ?? null,
    expectedArrival: booking.expectedArrivalAt ?? null,
    arrivalMode: booking.arrivalMode ?? null,
    vip: booking.isVip === true,
    vipContacts: (booking.vipContacts ?? []).map((v) => ({
      contactId: v.contactId,
      alertedAt: v.alertedAt ?? null,
    })),
    vipAlertedAt: booking.vipAlertedAt ?? null,
    guestCard: rateCard ? `${rateCard.name} (v${rateCard.version})` : null,

    rateCard: rateCard ?? null,

    offsiteTicket: booking.offsiteTickets?.[0] ?? null,
    hasOffsiteTicket: (booking.offsiteTickets ?? []).length > 0,

    /* Where the guest card parks the car. Flat rather than nested, because the
       runner queue is served the same row off a projection with no rate card.
       The id is cardLotId: plain lotId is the lot the car actually went to. */
    cardLotId: rateCard?.lot?.id ?? null,
    lotName: rateCard?.lot?.name ?? null,
    lotType: rateCard?.lot?.type ?? null,

    paymentRequired: billsGuest(rateCard),
    payLater: booking.payLater === true,
    paid: payment?.isPaid === true,
    amount: toAmount(payment?.amountAud),
    paymentStatus: payment?.lastChargeStatus ?? null,

    // Untyped JSONB straight off the booking, so read it defensively.
    cardLast4: booking.stripeSource?.card?.last4 ?? null,

    // Edit form inputs. bookingStatus, not status: on a valet arrival `status`
    // is the session's.
    guest: guestColumns(booking),
    rateCardId: booking.rateCardId ?? null,
    stripeCustomerId: booking.stripeCustomerId ?? null,
    bookingStatus: booking.status ?? null,

    isReturn: booking.status === "CURRENT",

    hasBooking: true,
    /* An expected arrival is reached through its booking, not a camera read,
     * so there is no session to have matched a plate against. */
    sanctionedPlate: null,
  };
}

function clockLabel(iso, timeZone) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const parts = { hour: "numeric", minute: "2-digit", hour12: true };

  try {
    return new Intl.DateTimeFormat("en-US", { ...parts, timeZone }).format(date);
  } catch (e) {
    /* An unknown IANA id throws RangeError - the device clock beats no hours. */
    return new Intl.DateTimeFormat("en-US", parts).format(date);
  }
}

/*
 * The hours collections run, e.g. "6:00 AM – 10:00 PM".
 *
 * Empty when the venue reports no windows, so the caller leaves the sentence
 * out rather than printing an empty range.
 */
export function formatCollectionHours(windows, timeZone) {
  if (!windows?.length) return "";

  const spans = windows
    .map(({ startsAt, endsAt }) => {
      const from = clockLabel(startsAt, timeZone);
      const to = clockLabel(endsAt, timeZone);

      return from && to ? `${from} – ${to}` : "";
    })
    .filter(Boolean);

  return [...new Set(spans)].join(", ");
}

/* Amounts arrive as decimal strings (or null when nothing succeeded). */
export function formatMoney(amount, currency) {
  const value = Number(amount ?? 0);
  const safe = Number.isFinite(value) ? value : 0;

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "AUD",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: safe % 1 === 0 ? 0 : 2,
    }).format(safe);
  } catch (e) {
    return `$${safe.toFixed(2)}`;
  }
}
